import { CompletedSteps, ProvetDialogStep, ProvetFormData } from "../types";

// Action type enum
export enum ProvetBookingFlowActionType {
  SetActiveStep = "SET_ACTIVE_STEP",
  SetCompletedSteps = "SET_COMPLETED_STEPS",
  SetFormData = "SET_FORM_DATA",
  ResetBooking = "RESET_BOOKING",
  SetBookingSubmitted = "SET_BOOKING_SUBMITTED",
  SetLoadingAvailableAppointmentTimes = "SET_LOADING_AVAILABLE_APPOINTMENT_TIMES",
  IncrementFormResetCounter = "INCREMENT_FORM_RESET_COUNTER",
  SetSpeciesIncompatibleWarning = "SET_SPECIES_INCOMPATIBLE_WARNING",
  SetCancelToken = "SET_CANCEL_TOKEN"
}

// Booking flow state interface
export interface ProvetBookingFlowState {
  activeStep: ProvetDialogStep;
  completedSteps: CompletedSteps;
  formData: ProvetFormData;
  bookingSubmitted: boolean;
  loadingAvailableAppointmentTimes: boolean;
  formResetCounter: number;
  showSpeciesIncompatibleWarning: boolean;
  /**
   * The secret from the booking response that lets this browser cancel the booking. Kept only in
   * memory: never store it, log it or send it to tracking.
   */
  cancelToken?: string;
}

// Action types
export type ProvetBookingFlowAction =
  | { type: ProvetBookingFlowActionType.SetActiveStep; step: ProvetDialogStep }
  | {
      type: ProvetBookingFlowActionType.SetCompletedSteps;
      completedSteps: Partial<ProvetBookingFlowState["completedSteps"]>;
    }
  | {
      type: ProvetBookingFlowActionType.SetFormData;
      formData: Partial<ProvetBookingFlowState["formData"]>;
    }
  | { type: ProvetBookingFlowActionType.ResetBooking; sessionId: string }
  | { type: ProvetBookingFlowActionType.SetBookingSubmitted; submitted: boolean }
  | {
      type: ProvetBookingFlowActionType.SetLoadingAvailableAppointmentTimes;
      loading: boolean;
    }
  | { type: ProvetBookingFlowActionType.IncrementFormResetCounter }
  | { type: ProvetBookingFlowActionType.SetSpeciesIncompatibleWarning; show: boolean }
  | { type: ProvetBookingFlowActionType.SetCancelToken; cancelToken: string | undefined };

// Initial state factory
export function getInitialProvetBookingFlowState(
  sessionId: string
): ProvetBookingFlowState {
  return {
    activeStep: ProvetDialogStep.SelectClinic,
    completedSteps: {
      selectClinic: false,
      selectTreatment: false,
      selectDateAndTime: false,
      verifyPhone: false,
      selectContactDetails: false
    },
    formData: {
      sessionId,
      selectedDepartment: undefined,
      selectedReason: undefined,
      selectedAppointmentTime: undefined,
      contactDetails: undefined,
      phoneVerified: false,
      verificationRequestId: undefined
    },
    bookingSubmitted: false,
    loadingAvailableAppointmentTimes: false,
    formResetCounter: 0,
    showSpeciesIncompatibleWarning: false
  };
}

// Reducer implementation
export function provetBookingFlowReducer(
  state: ProvetBookingFlowState,
  action: ProvetBookingFlowAction
): ProvetBookingFlowState {
  switch (action.type) {
    case ProvetBookingFlowActionType.SetActiveStep:
      return {
        ...state,
        activeStep: action.step
      };
    case ProvetBookingFlowActionType.SetCompletedSteps:
      return {
        ...state,
        completedSteps: {
          ...state.completedSteps,
          ...action.completedSteps
        }
      };
    case ProvetBookingFlowActionType.SetFormData:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.formData
        }
      };
    case ProvetBookingFlowActionType.ResetBooking:
      return getInitialProvetBookingFlowState(action.sessionId);
    case ProvetBookingFlowActionType.SetBookingSubmitted:
      return {
        ...state,
        bookingSubmitted: action.submitted
      };
    case ProvetBookingFlowActionType.SetLoadingAvailableAppointmentTimes:
      return {
        ...state,
        loadingAvailableAppointmentTimes: action.loading
      };
    case ProvetBookingFlowActionType.IncrementFormResetCounter:
      return {
        ...state,
        formResetCounter: state.formResetCounter + 1
      };
    case ProvetBookingFlowActionType.SetSpeciesIncompatibleWarning:
      return {
        ...state,
        showSpeciesIncompatibleWarning: action.show
      };
    case ProvetBookingFlowActionType.SetCancelToken:
      return {
        ...state,
        cancelToken: action.cancelToken
      };
    default:
      return state;
  }
}
